import React, { FC, useCallback, useMemo } from 'react'
import { cn } from 'shared/utils'
import { Table, Tag, Tooltip } from '@wmgtech/legato'

import { ConditionRender } from 'components'
import GpsBadge from 'components/GpsBadge/GpsBadge'
import { kpiFromFacts, latestChartForFactSource, peakFact, previousFact } from 'shared/utils/facts'
import { configMap } from 'modules/Products/constants'
import { capitalizeFirstLetter } from 'shared/utils/capitalizeFirstLetter'
import { formatDate } from 'shared/utils/formatDate'
import { getCoverImagePath } from 'shared/utils/getCoverImagePath'
import { CompactFactsItem, FactsItemWrapper } from '..'

import { IKpiItem, IProduct } from 'shared/interfaces'
import { ColumnsType } from '@wmgtech/legato/build/components/Table/types'
import { ProductItemsProps } from './ProductItems.props'

import './ProductItems.scss'

const ProductItems: FC<ProductItemsProps> = ({
  products,
  factSources,
  sources,
  countryCode,
  isExtended,
  isCompact,
  chartFreshness,
  label,
  isSameLabel
}) => {
  const productKpis = useCallback(
    (product: IProduct) => kpiFromFacts(product.facts || [], factSources, countryCode, product.configuration),
    [countryCode, factSources]
  )

  const baseColumn = useMemo(
    () => [
      {
        dataIndex: 'logo',
        key: 'logo',
        title: '',
        sortable: false,
        width: 70,
        align: 'right',
        render: (value: any, product: any) => (
          <img
            className={cn('products-items__image', { 'products-items__image--extended': isExtended })}
            alt={product.name}
            src={getCoverImagePath(product.parent_id)}
          />
        )
      },
      {
        dataIndex: 'name',
        key: 'name',
        title: 'Title',
        sortable: false,
        width: isCompact ? '65%' : isExtended ? '20%' : '40%',
        align: 'left',
        render: (value: any, product: any) => (
          <>
            <div className={cn('products-items__title', { 'products-items__title__extended': isExtended })}>
              <b>{product.name}</b>
            </div>
            <div className="products-items__badges">
              <GpsBadge gpsStatus={product.gps_status} coloredBackground={true} />
            </div>
          </>
        )
      },
      ...(label
        ? [
          {
            dataIndex: 'label',
            key: 'label',
            title: 'Zone',
            sortable: false,
            width: '10%',
            align: 'left',
            render: () => (
              <Tag
                size={'xs'}
                removable={false}
                rounded
                text={label.alias}
                colorType={isSameLabel ? 'white' : 'dark'}
                containerStyle={isSameLabel ? 'outline' : 'solid'}
                className="products-items__label"
                tooltip
                tooltipText={label.name}
              />
            )
          }
        ]
        : []),
      {
        dataIndex: 'configuration',
        key: 'configuration',
        title: 'Type',
        sortable: false,
        width: isExtended ? '15%' : '20%',
        align: 'left',
        render: (value: string) => <div className="products-items__type">{value}</div>
      }
    ],
    [isExtended, isCompact, label, isSameLabel]
  )
  const extendedColumn = useMemo(
    (): any =>
      sources.map((source) => {
        const kpi = configMap.find((config) => source.includes(config.name))

        return {
          dataIndex: kpi?.name,
          key: kpi?.name,
          title: (
            <Tooltip className="products-items__tooltip" message={kpi?.title || ''} placement="top" colorType="dark">
              <img className="products-items__icon" src={kpi?.iconPath} alt={kpi?.title} />
            </Tooltip>
          ),
          sortable: false,
          align: 'center',
          width: 60,
          render: (kpi: any, product: any) => (
            <FactsItemWrapper
              previousFact={previousFact(product.facts, kpi?.fact?.chart || '')}
              kpiItem={kpi || ({ source: {} } as IKpiItem)}
              peakFact={kpi?.fact && peakFact(product.facts, kpi.fact.chart)}
              latestChart={latestChartForFactSource(source, product.configuration, chartFreshness)}
              render={({ fact, isOutdated, trend, peakFact, source }) => (
                <CompactFactsItem
                  fact={fact}
                  isOutdated={isOutdated}
                  trend={trend}
                  peakFact={peakFact}
                  source={source}
                  countryCode={countryCode}
                  isExtended={isExtended}
                />
              )}
            />
          )
        }
      }),
    [isExtended, sources, countryCode, chartFreshness]
  )

  const nonCompactColumn = useMemo(
    () => [
      {
        dataIndex: 'publish_date',
        key: 'publish_date',
        title: 'Release Date',
        sortable: false,
        align: 'left',
        ...(isExtended && { width: '15%' })
      }
    ],
    [isExtended]
  )

  const columns: ColumnsType<unknown>[] = useMemo(() => {
    let currentColumns = isExtended ? [...baseColumn, ...extendedColumn] : baseColumn
    currentColumns = !isCompact ? [...currentColumns, ...nonCompactColumn] : currentColumns
    return currentColumns
  }, [isExtended, isCompact, baseColumn, nonCompactColumn, extendedColumn])

  const productsListRows = useMemo(
    () =>
      products
        .filter(({ active }) => active)
        .map((product) => ({
          ...product,
          name: product.name,
          configuration: capitalizeFirstLetter(product.configuration),
          publish_date: formatDate({ date: product.publish_date, withTime: false }),
          ...sources.reduce((result, source) => {
            const key = source.split('_')?.[0] || ''
            const kpi = productKpis(product).find((item) => item.source.chart === source)

            return { ...result, [key]: kpi }
          }, {})
        })),
    [products, sources, productKpis]
  )

  return (
    <div className="products-items">
      <ConditionRender
        condition={!isExtended}
        component={
          <h3
            className={cn('products-items__header-title', {
              'products-items__header-title--compact': isCompact
            })}
          >
            Products
          </h3>
        }
      />
      <Table
        rowKey={(record: any) => record.id}
        key={sources.length}
        condensed
        columns={columns}
        tableLayout="fixed"
        data={productsListRows}
        striped="odd"
        pagination={false}
      />
    </div>
  )
}

export default ProductItems
